import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import { cn } from '@/lib/utils';

/* Schermate di BOSS ricostruite per il sito, una per funzionalità.
   I dati sono di esempio: nessun nome o importo viene da clienti veri. */

const LABEL = 'font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground';

export function AppWindow({ title, children, className }: { title: string; children: ReactNode; className?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className={cn(
        'overflow-hidden rounded-[14px] border border-input bg-card shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6),0_8px_24px_-12px_rgba(0,0,0,0.5)]',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-3 border-b border-border bg-secondary px-4 py-2.5">
        <div className="flex shrink-0 items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-destructive" />
          <span className="h-2.5 w-2.5 rounded-full bg-warning" />
          <span className="h-2.5 w-2.5 rounded-full bg-success" />
        </div>
        <div className="truncate font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          boss · {title}
        </div>
        <div className="w-12 shrink-0" />
      </div>
      <div className="p-4 md:p-6">{children}</div>
    </motion.div>
  );
}

function Panel({ label, right, children, className }: { label: string; right?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-lg border border-border bg-background/40 p-4', className)}>
      <div className="flex items-center justify-between gap-2">
        <span className={LABEL}>{label}</span>
        {right}
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Chip({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'primary' | 'success' | 'warning' | 'danger' }) {
  const tones = {
    neutral: 'border-border text-fg-2',
    primary: 'border-primary/30 bg-primary/10 text-primary',
    success: 'border-success/30 bg-success/10 text-success',
    warning: 'border-warning/30 bg-warning/10 text-warning',
    danger: 'border-destructive/30 bg-destructive/10 text-destructive',
  };
  return (
    <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em] whitespace-nowrap', tones[tone])}>
      {children}
    </span>
  );
}

function Kpi({ label, value, note, tone }: { label: string; value: string; note?: string; tone?: 'success' | 'warning' }) {
  return (
    <div className="rounded-lg border border-border bg-background/40 p-3">
      <div className={LABEL}>{label}</div>
      <div className={cn('mt-1.5 text-[22px] font-light leading-none tabular-nums', tone === 'success' && 'text-success', tone === 'warning' && 'text-warning')}>
        {value}
      </div>
      {note && <div className="mt-1.5 font-mono text-[9px] text-muted-foreground">{note}</div>}
    </div>
  );
}

function Bar({ value, tone = 'primary' }: { value: number; tone?: 'primary' | 'warning' | 'success' }) {
  const bg = { primary: 'bg-primary', warning: 'bg-warning', success: 'bg-success' }[tone];
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-border">
      <motion.div
        initial={{ width: 0 }}
        whileInView={{ width: `${Math.min(value, 100)}%` }}
        viewport={{ once: true }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
        className={cn('h-full rounded-full', bg)}
      />
    </div>
  );
}

/* 01 · Gestione della commessa */
function CommessaVisual() {
  const tabs = ['Cockpit', 'Anagrafica', 'Organigramma', 'Documenti', 'Email', 'Compiti'];
  return (
    <AppWindow title="commessa CM-101">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className={LABEL}>CM-101 · Borgo Alto</div>
          <div className="mt-1 text-[18px] text-foreground md:text-[22px]">Scuola media — adeguamento sismico</div>
        </div>
        <Chip tone="success">In linea</Chip>
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {tabs.map((t, i) => (
          <span
            key={t}
            className={cn(
              'rounded-full border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.12em]',
              i === 0 ? 'border-primary/30 bg-primary/10 text-primary' : 'border-border text-fg-2',
            )}
          >
            {t}
          </span>
        ))}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-4">
        <Kpi label="Importo contratto" value="€ 2,80M" />
        <Kpi label="Costo a oggi" value="€ 1,52M" note="su preventivo € 2,31M" />
        <Kpi label="Margine" value="17,4%" note="+0,8 pt nel mese" tone="success" />
        <Kpi label="Avanzamento" value="68%" note="SAL 5 a novembre" />
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
        <Panel label="Organigramma">
          <ul className="space-y-2 text-[12px]">
            {[
              ['Project manager', 'G. Di Marco'],
              ['Direttore tecnico', 'L. Ferri'],
              ['Capocantiere', 'A. Rossi'],
              ['Sicurezza (CSE)', 'M. Conti'],
            ].map(([ruolo, nome]) => (
              <li key={ruolo} className="flex items-center justify-between gap-2">
                <span className="text-fg-2">{ruolo}</span>
                <span className="text-foreground">{nome}</span>
              </li>
            ))}
          </ul>
        </Panel>
        <Panel label="Compiti aperti" right={<Chip tone="primary">3</Chip>}>
          <ul className="space-y-2 text-[12px]">
            {[
              ['Inviare SAL 5 al committente', 'entro 05/11', false],
              ['Verbale di consegna lavori Ala B', 'entro 28/10', false],
              ['Rinnovo POS subappaltatore', 'entro 31/10', false],
              ['Ordine ferro Lotto 2', 'fatto', true],
            ].map(([t, d, done]) => (
              <li key={t as string} className="flex items-start gap-2">
                <span className={cn('mt-0.5 h-3 w-3 shrink-0 rounded-[3px] border', done ? 'border-success bg-success/30' : 'border-input')} />
                <span className={cn('flex-1', done ? 'text-muted-foreground line-through' : 'text-foreground')}>{t}</span>
                <span className="font-mono text-[9px] text-muted-foreground">{d}</span>
              </li>
            ))}
          </ul>
        </Panel>
        <Panel label="Email della commessa">
          <ul className="space-y-2.5 text-[12px]">
            {[
              ['Comune di Borgo Alto', 'Approvazione variante n. 2', '10:42'],
              ['Edil Demo', 'Conferma consegna ferro', 'ieri'],
              ['Studio tecnico associato', 'Verbale riunione di cantiere', 'lun'],
            ].map(([da, ogg, quando]) => (
              <li key={ogg}>
                <div className="flex justify-between gap-2">
                  <span className="truncate text-foreground">{da}</span>
                  <span className="font-mono text-[9px] text-muted-foreground">{quando}</span>
                </div>
                <div className="truncate text-fg-2">{ogg}</div>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </AppWindow>
  );
}

/* 02 · Contabilità analitica */
function ContabilitaVisual() {
  const voci = [
    { voce: 'Manodopera', prev: 610, oggi: 402, avanz: 66 },
    { voce: 'Subappalti', prev: 820, oggi: 571, avanz: 70 },
    { voce: 'Materiali', prev: 540, oggi: 395, avanz: 73 },
    { voce: 'Mezzi e carburanti', prev: 160, oggi: 128, avanz: 80, warn: true },
    { voce: 'Servizi', prev: 120, oggi: 41, avanz: 34 },
    { voce: 'Altri costi', prev: 60, oggi: 22, avanz: 37 },
  ];
  const mesi = [
    { m: 'mag', prod: 38, costo: 31 },
    { m: 'giu', prod: 52, costo: 44 },
    { m: 'lug', prod: 61, costo: 50 },
    { m: 'ago', prod: 33, costo: 30 },
    { m: 'set', prod: 70, costo: 57 },
    { m: 'ott', prod: 64, costo: 52 },
  ];
  return (
    <AppWindow title="quadro economico · CM-101">
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <Kpi label="Valore prodotto" value="€ 1,84M" />
        <Kpi label="Costo a oggi" value="€ 1,52M" />
        <Kpi label="Fatturato" value="€ 1,40M" />
        <Kpi label="Margine operativo" value="17,4%" tone="success" note="certo 15,1% · provvisorio 2,3%" />
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-[1.5fr_1fr]">
        <Panel label="Costi per voce · € migliaia">
          <div className="hidden grid-cols-[1.4fr_0.6fr_0.6fr_1fr] gap-3 pb-2 md:grid">
            {['Voce', 'Preventivo', 'A oggi', 'Consumo budget'].map((h) => (
              <span key={h} className={LABEL}>{h}</span>
            ))}
          </div>
          <ul className="divide-y divide-border">
            {voci.map((v) => (
              <li key={v.voce} className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1.5 py-2 text-[12px] md:grid-cols-[1.4fr_0.6fr_0.6fr_1fr]">
                <span className="text-foreground">{v.voce}</span>
                <span className="hidden tabular-nums text-fg-2 md:block">{v.prev}</span>
                <span className="tabular-nums text-foreground">
                  {v.oggi}
                  <span className="text-muted-foreground md:hidden"> / {v.prev}</span>
                </span>
                <div className="col-span-2 flex items-center gap-2 md:col-span-1">
                  <Bar value={(v.oggi / v.prev) * 100} tone={v.warn ? 'warning' : 'primary'} />
                  <span className={cn('w-9 text-right font-mono text-[10px]', v.warn ? 'text-warning' : 'text-muted-foreground')}>
                    {Math.round((v.oggi / v.prev) * 100)}%
                  </span>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-2 flex items-start gap-2 rounded-md border border-warning/30 bg-warning/[0.06] px-3 py-2 text-[11px] text-fg-2">
            <span className="text-warning">!</span>
            Mezzi all’80% del budget con il 68% dei lavori: scostamento da verificare.
          </div>
        </Panel>

        <Panel label="Prodotto e costo per mese">
          <div className="flex h-[150px] items-end gap-2">
            {mesi.map((m, i) => (
              <div key={m.m} className="flex flex-1 flex-col items-center gap-1.5">
                <div className="flex h-[124px] w-full items-end justify-center gap-[3px]">
                  {[
                    { h: m.prod, c: 'bg-primary' },
                    { h: m.costo, c: 'bg-fg-disabled' },
                  ].map((b, j) => (
                    <motion.div
                      key={j}
                      initial={{ height: 0 }}
                      whileInView={{ height: `${b.h * 1.6}px` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.2 + i * 0.06 }}
                      className={cn('w-[40%] max-w-[14px] rounded-t-[3px]', b.c)}
                    />
                  ))}
                </div>
                <span className="font-mono text-[9px] uppercase text-muted-foreground">{m.m}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 flex gap-4 text-[11px] text-fg-2">
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-primary" />Valore prodotto</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-fg-disabled" />Costo</span>
          </div>
        </Panel>
      </div>
    </AppWindow>
  );
}

/* 03 · Presenze e buste paga */
type Cella = { v: string; tone?: 'over' | 'abs' | 'rain' | 'err' };
const GIORNI = ['L 13', 'M 14', 'M 15', 'G 16', 'V 17', 'L 20', 'M 21'];
const PERSONE: { nome: string; ccnl: string; celle: Cella[] }[] = [
  { nome: 'Rossi A.', ccnl: 'Edile', celle: [{ v: '8' }, { v: '9', tone: 'over' }, { v: '8' }, { v: 'P', tone: 'rain' }, { v: '8' }, { v: '8' }, { v: '10', tone: 'over' }] },
  { nome: 'Bianchi M.', ccnl: 'Edile', celle: [{ v: '8' }, { v: '8' }, { v: '8' }, { v: 'P', tone: 'rain' }, { v: '8' }, { v: 'F', tone: 'abs' }, { v: 'F', tone: 'abs' }] },
  { nome: 'Esposito G.', ccnl: 'Metalm.', celle: [{ v: '8' }, { v: '8' }, { v: '13', tone: 'err' }, { v: '8' }, { v: '7' }, { v: '8' }, { v: '8' }] },
  { nome: 'Ricci L.', ccnl: 'Metalm.', celle: [{ v: '8' }, { v: '8' }, { v: '8' }, { v: '8' }, { v: '8' }, { v: '9', tone: 'over' }, { v: '' , tone: 'err' }] },
  { nome: 'Greco F.', ccnl: 'Edile', celle: [{ v: 'PR', tone: 'abs' }, { v: '8' }, { v: '8' }, { v: 'P', tone: 'rain' }, { v: '8' }, { v: '8' }, { v: '8' }] },
];
const TONI: Record<NonNullable<Cella['tone']>, string> = {
  over: 'bg-primary/15 text-primary',
  abs: 'bg-success/10 text-success',
  rain: 'bg-secondary text-fg-2',
  err: 'bg-destructive/15 text-destructive ring-1 ring-inset ring-destructive/50',
};

function PresenzeGrid({ compact = false }: { compact?: boolean }) {
  const giorni = compact ? GIORNI.slice(0, 5) : GIORNI;
  return (
    <div>
      <div className="grid gap-px overflow-hidden rounded-md border border-border bg-border text-[11px]" style={{ gridTemplateColumns: `minmax(84px,1.6fr) repeat(${giorni.length}, minmax(0,1fr))` }}>
        <div className={cn(LABEL, 'bg-secondary px-2 py-1.5')}>Persona</div>
        {giorni.map((g) => (
          <div key={g} className={cn(LABEL, 'bg-secondary px-1 py-1.5 text-center')}>{g}</div>
        ))}
        {PERSONE.map((p) => (
          <div key={p.nome} className="contents">
            <div className="bg-background px-2 py-1.5">
              <div className="truncate text-foreground">{p.nome}</div>
              <div className="font-mono text-[8px] uppercase tracking-[0.12em] text-muted-foreground">{p.ccnl}</div>
            </div>
            {p.celle.slice(0, giorni.length).map((c, i) => (
              <div key={i} className={cn('flex items-center justify-center bg-background font-mono tabular-nums text-fg-2', c.tone && TONI[c.tone])}>
                {c.v || '—'}
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-fg-2">
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-primary/40" />Straordinario</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-success/40" />Ferie / permesso</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-fg-disabled" />P = pioggia</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-destructive/60" />Da verificare</span>
      </div>
    </div>
  );
}

function FileDelMese() {
  const files = [
    'Proposta presenze 2026-10 · Inaz.xlsx',
    'Proposta presenze 2026-10 · TeamSystem.xlsx',
    'Prospetto viaggi · Edili.xlsx',
    'Prospetto CIG maltempo.xlsx',
    'Agenzia interinale · riepilogo.xlsx',
  ];
  return (
    <div className="space-y-1.5">
      {files.map((f) => (
        <div key={f} className="flex items-center gap-2 rounded-md border border-border bg-background/40 px-2.5 py-1.5 text-[11px] text-fg-2">
          <span className="font-mono text-[8px] uppercase text-success">xlsx</span>
          <span className="truncate">{f}</span>
        </div>
      ))}
      <div className="mt-3 flex items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-[12px] font-medium text-primary-foreground">
        Scarica tutto (.zip) <span aria-hidden>↓</span>
      </div>
    </div>
  );
}

function PresenzeVisual() {
  return (
    <AppWindow title="estrazione mensile · ottobre 2026">
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.6fr_1fr]">
        <Panel label="Presenze del mese · ore per giorno" right={<Chip>2 società · 2 CCNL</Chip>}>
          <PresenzeGrid />
        </Panel>
        <div className="space-y-3">
          <Panel label="Controlli prima di scaricare" right={<Chip tone="danger">2 da verificare</Chip>}>
            <ul className="space-y-1.5 text-[11px]">
              <li className="flex gap-2 text-destructive"><span>●</span><span className="text-fg-2">Esposito G. · 15/10 · <span className="text-foreground">13 ore in una giornata</span></span></li>
              <li className="flex gap-2 text-destructive"><span>●</span><span className="text-fg-2">Ricci L. · 21/10 · <span className="text-foreground">giornata incompleta</span></span></li>
              <li className="flex gap-2 text-success"><span>●</span><span className="text-fg-2">Codici dipendente, cantieri e voci di paga: tutti trovati</span></li>
            </ul>
          </Panel>
          <Panel label="File per il consulente">
            <FileDelMese />
          </Panel>
        </div>
      </div>
    </AppWindow>
  );
}

/* Versione ridotta per la Home. */
export function PresenzeMini() {
  return (
    <AppWindow title="estrazione mensile · ottobre 2026">
      <PresenzeGrid compact />
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <Chip tone="danger">2 da verificare</Chip>
        <span className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-[12px] font-medium text-primary-foreground">
          Scarica il file per il consulente <span aria-hidden>↓</span>
        </span>
      </div>
    </AppWindow>
  );
}

/* 04 · Personale */
function PersonaleVisual() {
  const costo = [
    ['Paga base e contingenza', '13,42'],
    ['Ratei 13ª e 14ª', '2,24'],
    ['Contributi INPS e INAIL', '6,18'],
    ['TFR', '1,15'],
    ['Oneri contrattuali di settore', '1,73'],
  ];
  return (
    <AppWindow title="gestione personale · scheda dipendente">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_1.2fr]">
        <Panel label="Dipendente">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 font-mono text-[12px] text-primary">AR</div>
            <div>
              <div className="text-[15px] text-foreground">Andrea Rossi</div>
              <div className="text-[11px] text-fg-2">Operaio specializzato · 3° livello</div>
            </div>
          </div>
          <dl className="mt-4 grid grid-cols-2 gap-y-2 text-[11px]">
            {[
              ['CCNL', 'Edilizia industria'],
              ['Società', 'Impresa Esempio S.r.l.'],
              ['Assunto il', '04/03/2019'],
              ['Contratto', 'Tempo indeterminato'],
            ].map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="text-foreground">{v}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-4 border-t border-border pt-3">
            <div className={LABEL}>Scadenze</div>
            <ul className="mt-2 space-y-1.5 text-[11px]">
              <li className="flex justify-between"><span className="text-fg-2">Visita medica</span><Chip tone="warning">tra 12 giorni</Chip></li>
              <li className="flex justify-between"><span className="text-fg-2">Corso ponteggi</span><Chip tone="success">valido</Chip></li>
            </ul>
          </div>
        </Panel>
        <Panel label="Costo orario da CCNL · €/h" right={<Chip tone="primary">tabelle ministeriali</Chip>}>
          <ul className="space-y-2 text-[12px]">
            {costo.map(([k, v]) => (
              <li key={k} className="flex items-center gap-3">
                <span className="flex-1 text-fg-2">{k}</span>
                <span className="w-24"><Bar value={(parseFloat(v.replace(',', '.')) / 13.42) * 100} /></span>
                <span className="w-12 text-right tabular-nums text-foreground">{v}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex items-end justify-between border-t border-border pt-3">
            <span className="text-[12px] text-fg-2">Costo orario in commessa</span>
            <span className="text-[30px] font-light leading-none tabular-nums text-foreground">€ 24,72</span>
          </div>
        </Panel>
      </div>
    </AppWindow>
  );
}

/* 05 · Acquisti e DDT */
function DdtVisual() {
  const righe = [
    { art: 'Tondino B450C Ø12', ddt: '2.400 kg', oda: '2.400 kg', ok: true },
    { art: 'Rete elettrosaldata 20×20', ddt: '60 pz', oda: '60 pz', ok: true },
    { art: 'Distanziatori cls', ddt: '400 pz', oda: '500 pz', ok: false },
    { art: 'Filo di ferro cotto', ddt: '25 kg', oda: '25 kg', ok: true },
  ];
  return (
    <AppWindow title="DDT n. 4521 · letto dall’AI">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-[0.9fr_1.4fr]">
        {/* Il documento */}
        <div className="relative rounded-lg border border-border bg-[#f4f1ea] p-4 text-[#2a2a2a]">
          <div className="flex justify-between">
            <div>
              <div className="text-[13px] font-semibold">EDIL DEMO S.r.l.</div>
              <div className="text-[9px] text-[#666]">P.IVA 00000000000 · Valverde</div>
            </div>
            <div className="text-right text-[9px] text-[#666]">
              Documento di trasporto
              <div className="mt-0.5 rounded-sm bg-primary/25 px-1 text-[11px] font-semibold text-[#2a2a2a] ring-1 ring-primary">n. 4521 · 22/10/2026</div>
            </div>
          </div>
          <div className="mt-3 text-[9px] text-[#666]">Destinazione: cantiere CM-101 — Borgo Alto</div>
          <div className="mt-3 space-y-1.5">
            {righe.map((r) => (
              <div key={r.art} className={cn('flex justify-between rounded-sm px-1 text-[10px]', !r.ok && 'bg-warning/30 ring-1 ring-warning')}>
                <span>{r.art}</span>
                <span className="tabular-nums">{r.ddt}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 h-6 w-24 border-b border-[#999] text-[8px] text-[#999]">firma</div>
        </div>

        {/* Quello che estrae BOSS */}
        <div className="space-y-3">
          <Panel label="Dati estratti" right={<Chip tone="success">affidabilità 98%</Chip>}>
            <dl className="grid grid-cols-2 gap-y-1.5 text-[11px] md:grid-cols-4">
              {[
                ['Fornitore', 'Edil Demo'],
                ['Numero', '4521'],
                ['Data', '22/10/2026'],
                ['Ordine', 'ODA 118'],
              ].map(([k, v]) => (
                <div key={k}>
                  <dt className="text-muted-foreground">{k}</dt>
                  <dd className="text-foreground">{v}</dd>
                </div>
              ))}
            </dl>
          </Panel>
          <Panel label="Confronto con l’ordine ODA 118">
            <ul className="divide-y divide-border text-[11px]">
              {righe.map((r) => (
                <li key={r.art} className="flex items-center gap-2 py-1.5">
                  <span className={r.ok ? 'text-success' : 'text-warning'}>{r.ok ? '✓' : '!'}</span>
                  <span className="flex-1 truncate text-foreground">{r.art}</span>
                  <span className="tabular-nums text-fg-2">{r.ddt}</span>
                  <span className="hidden w-20 text-right tabular-nums text-muted-foreground sm:block">ord. {r.oda}</span>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="rounded-md bg-primary px-3 py-1.5 text-[11px] font-medium text-primary-foreground">Imputa alla commessa</span>
              <span className="rounded-md border border-input px-3 py-1.5 text-[11px] text-foreground">Contesta 1 riga</span>
            </div>
          </Panel>
        </div>
      </div>
    </AppWindow>
  );
}

/* 06 · Gare e offerte */
function GareVisual() {
  const colonne = [
    { fase: 'Segnalata', items: [['Asilo comunale — Pianello', '€ 1,2M'], ['Rete fognaria — zona nord', '€ 3,4M']] },
    { fase: 'In studio', items: [['Polo scolastico — Valverde', '€ 6,1M']] },
    { fase: 'Offerta inviata', items: [['Ponte sul torrente — manutenzione', '€ 2,7M'], ['Uffici pubblici — efficientamento', '€ 0,9M']] },
    { fase: 'Aggiudicata', items: [['Scuola media — adeguamento sismico', '€ 2,8M']], win: true },
  ];
  return (
    <AppWindow title="gare e opportunità">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {colonne.map((c) => (
          <div key={c.fase} className="rounded-lg border border-border bg-background/40 p-3">
            <div className="flex items-center justify-between">
              <span className={LABEL}>{c.fase}</span>
              <span className="font-mono text-[10px] text-muted-foreground">{c.items.length}</span>
            </div>
            <div className="mt-3 space-y-2">
              {c.items.map(([nome, importo]) => (
                <div key={nome} className={cn('rounded-md border bg-card p-2.5', c.win ? 'border-success/40' : 'border-border')}>
                  <div className="text-[12px] leading-snug text-foreground">{nome}</div>
                  <div className="mt-1.5 flex items-center justify-between">
                    <span className="font-mono text-[10px] tabular-nums text-fg-2">{importo}</span>
                    {c.win ? <Chip tone="success">vinta</Chip> : <span className="font-mono text-[9px] text-muted-foreground">✉ 3</span>}
                  </div>
                  {c.win && (
                    <div className="mt-2.5 rounded-md bg-primary px-2 py-1.5 text-center text-[11px] font-medium text-primary-foreground">
                      Crea la commessa →
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </AppWindow>
  );
}

/* 07 · Direzione */
function DirezioneVisual() {
  const aree = [
    { area: 'Edilizia', commesse: 31, margine: '16,8%', avanz: 58 },
    { area: 'Reti e impianti', commesse: 27, margine: '12,1%', avanz: 64, warn: true },
    { area: 'Manutenzioni', commesse: 14, margine: '21,4%', avanz: 71 },
  ];
  const commesse = [
    ['CM-101', 'Scuola media — adeg. sismico', '17,4%', 'ok'],
    ['CM-102', 'Rete idrica — Lotto 3', '8,2%', 'warn'],
    ['CM-103', 'Residenze del Parco', '19,0%', 'ok'],
    ['CM-104', 'Rete gas — allacci', '14,6%', 'late'],
  ];
  return (
    <AppWindow title="direzione · panoramica">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
        {aree.map((a) => (
          <div key={a.area} className="rounded-lg border border-border bg-background/40 p-3">
            <div className="flex items-center justify-between">
              <span className={LABEL}>{a.area}</span>
              <span className="font-mono text-[10px] text-muted-foreground">{a.commesse} commesse</span>
            </div>
            <div className={cn('mt-2 text-[26px] font-light leading-none tabular-nums', a.warn ? 'text-warning' : 'text-success')}>{a.margine}</div>
            <div className="mt-1 font-mono text-[9px] text-muted-foreground">margine medio</div>
            <div className="mt-3 flex items-center gap-2">
              <Bar value={a.avanz} />
              <span className="font-mono text-[10px] text-muted-foreground">{a.avanz}%</span>
            </div>
          </div>
        ))}
      </div>
      <Panel label="Commesse da guardare oggi" className="mt-3">
        <ul className="divide-y divide-border text-[12px]">
          {commesse.map(([code, nome, marg, stato]) => (
            <li key={code} className="flex items-center gap-3 py-2">
              <span className="hidden w-16 font-mono text-[10px] text-muted-foreground sm:block">{code}</span>
              <span className="flex-1 truncate text-foreground">{nome}</span>
              <span className="tabular-nums text-fg-2">{marg}</span>
              {stato === 'ok' && <Chip tone="success">rapportini ok</Chip>}
              {stato === 'warn' && <Chip tone="warning">margine in calo</Chip>}
              {stato === 'late' && <Chip tone="danger">2 giorni non compilati</Chip>}
            </li>
          ))}
        </ul>
      </Panel>
    </AppWindow>
  );
}

const VISUALS: Record<string, () => ReactNode> = {
  'gestione-commessa': CommessaVisual,
  'contabilita-analitica': ContabilitaVisual,
  'presenze-buste-paga': PresenzeVisual,
  personale: PersonaleVisual,
  'acquisti-ddt': DdtVisual,
  'gare-offerte': GareVisual,
  direzione: DirezioneVisual,
};

export function FeatureVisual({ slug }: { slug: string }) {
  const V = VISUALS[slug];
  return V ? <V /> : null;
}
